import React, { useState } from 'react';
import _sortBy from 'lodash/sortBy';
import { useDeepCompareCallback } from 'use-deep-compare';

import { Checkbox, Input, NumberInput } from '@trqk/components';
import { locale } from '@trqk/utils';

import { StepWrapper } from '../..';
import { useGapAnalysisTools } from '../../../../../../hooks';

interface FilterStates {
    booleans: Record<string, boolean>;
    strings: Record<string, string>;
    numbers: Record<string, number>;
}

/**
 * Displays list of inputs the user can use to select options for a gap analysis report. This step
 * is rendered after the SetUp step only if `listGapAnalysisFilters` returns data. If not, the user
 * will go straight to `analyzingData`.
 * @param props - {@link GapAnalysisStepProps}
 */
export const SelectOptions: GapAnalysisStepComponent = props => {
    const { context, remove, send } = props;
    const { filterOptions, trqkGap } = context;
    const gapAnalysisId = trqkGap?.gap_analysis_id ?? null;
    const { analyzeTrqkGap } = useGapAnalysisTools();

    const [filterStates, setFilterStates] = useState<FilterStates>(
        filterOptions.reduce(
            (
                acc: FilterStates,
                {
                    filter_input_type,
                    filter_key,
                    filter_label,
                    filter_value_default,
                }: GapAnalysisFilter,
            ) => {
                if (!filter_key || !filter_label || !filter_input_type) {
                    return acc;
                }

                if (filter_input_type === 'CHECKBOX') {
                    acc.booleans = {
                        ...acc.booleans,
                        [filter_key]: filter_value_default === 'true',
                    };
                }

                if (filter_input_type === 'TEXT') {
                    acc.strings = {
                        ...acc.strings,
                        [filter_key]: filter_value_default ?? '',
                    };
                }

                if (filter_input_type === 'NUMBER') {
                    let parsed = parseInt(filter_value_default ?? '0', 10);

                    if (Number.isNaN(parsed)) {
                        parsed = 0;
                    }

                    acc.numbers = {
                        ...acc.numbers,
                        [filter_key]: parsed,
                    };
                }

                return acc;
            },
            {
                booleans: {},
                strings: {},
                numbers: {},
            },
        ),
    );

    const filters = _sortBy<GapAnalysisFilter>(
        filterOptions,
        filter => filter?.order ?? 0,
    ).reduce(
        (
            acc: JSX.Element[],
            { filter_input_type, filter_key, filter_label }: GapAnalysisFilter,
        ) => {
            if (!filter_key || !filter_label || !filter_input_type) {
                return acc;
            }

            const inputId = `analysis-options-filter-${filter_key}`;

            if (filter_input_type === 'CHECKBOX') {
                acc.push(
                    <Checkbox
                        addClasses="my-1"
                        key={filter_key}
                        label={filter_label}
                        inputId={inputId}
                        checked={filterStates.booleans[filter_key] ?? false}
                        onChange={e => {
                            const target = e.target as HTMLInputElement;

                            setFilterStates((prev: FilterStates) => ({
                                ...prev,
                                booleans: {
                                    ...prev.booleans,
                                    [filter_key]: target.checked,
                                },
                            }));
                        }}
                        size="large"
                        color="primary"
                        wrapLabel={true}
                    />,
                );
            }

            if (filter_input_type === 'TEXT') {
                acc.push(
                    <Input
                        addClasses="border border-tertiary-300 my-2"
                        key={filter_key}
                        label={filter_label}
                        inputId={inputId}
                        value={filterStates.strings[filter_key] ?? ''}
                        handleChange={value => {
                            setFilterStates((prev: FilterStates) => ({
                                ...prev,
                                strings: {
                                    ...prev.strings,
                                    [filter_key]: value,
                                },
                            }));
                        }}
                        padding="small"
                    />,
                );
            }

            if (filter_input_type === 'NUMBER') {
                acc.push(
                    <div>
                        <label htmlFor={inputId}>
                            {filter_label}
                            <NumberInput
                                addClasses="ml-2 border border-tertiary-300 my-2"
                                inputId={inputId}
                                key={filter_key}
                                value={filterStates.numbers[filter_key] ?? ''}
                                onChange={value => {
                                    setFilterStates((prev: FilterStates) => ({
                                        ...prev,
                                        numbers: {
                                            ...prev.numbers,
                                            [filter_key]: value,
                                        },
                                    }));
                                }}
                                padding="small"
                            />
                        </label>
                    </div>,
                );
            }

            return acc;
        },
        [],
    );

    const beforeNextStep = useDeepCompareCallback(
        (): Promise<boolean> =>
            new Promise(resolve => {
                const { booleans, numbers, strings } = filterStates;
                const booleanInputs = Object.entries(booleans).map(
                    ([filter_key, value]) => ({
                        filter_key,
                        filter_value: value.toString(),
                    }),
                );
                const numberInputs = Object.entries(numbers).map(
                    ([filter_key, value]) => ({
                        filter_key,
                        filter_value: value.toString(),
                    }),
                );
                const stringInputs = Object.entries(strings).map(
                    ([filter_key, value]) => ({
                        filter_key,
                        filter_value: value.toString(),
                    }),
                );

                const data = [
                    ...booleanInputs,
                    ...numberInputs,
                    ...stringInputs,
                ];

                analyzeTrqkGap(gapAnalysisId, {
                    data,
                })
                    .then(success => {
                        resolve(success);
                    })
                    .catch(() => {
                        resolve(false);
                    });
            }),
        [filterStates, gapAnalysisId],
    );

    return (
        <StepWrapper
            beforeNextStep={beforeNextStep}
            context={context}
            remove={remove}
            send={send}
            helpLinkHref="https://www.trqk.io/faq#publisher-edition-rules"
        >
            <div>
                <h4 className="mb-4 text-lg">
                    {
                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS.SELECT_OPTIONS
                            .DESCRIPTION
                    }
                </h4>
                <div className="space-y-1">{filters}</div>
            </div>
        </StepWrapper>
    );
};
